import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useWorkspace } from "../contexts/WorkspaceContext";
import {
  Home, Sparkles, Globe,
  Bell, LogOut, ShieldCheck, ChevronRight,
} from "lucide-react";
import Avatar from "./Avatar";
import VorionLogo from "./VorionLogo";

const NAV = [
  { to: "/home", label: "Home", icon: Home, testid: "nav-home" },
  { to: "/ai-assistant", label: "AI Assistant", icon: Sparkles, testid: "nav-ai" },
  { to: "/hotel-website", label: "Hotel Website", icon: Globe, testid: "nav-hotel-website" },
];

const IDLE_MS = 5000;

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { current } = useWorkspace();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const idleTimerRef = useRef(null);
  const asideRef = useRef(null);

  const clearIdle = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const armIdle = useCallback(() => {
    clearIdle();
    idleTimerRef.current = setTimeout(() => setOpen(false), IDLE_MS);
  }, [clearIdle]);

  const openDrawer = useCallback(() => {
    setOpen(true);
    armIdle();
  }, [armIdle]);

  const closeDrawer = useCallback(() => {
    clearIdle();
    setOpen(false);
  }, [clearIdle]);

  // Close on outside mousedown
  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (asideRef.current && !asideRef.current.contains(e.target)) {
        // Ignore clicks on the edge trigger (it has its own handler)
        if (!e.target.closest?.("[data-testid='sidebar-trigger']")) {
          closeDrawer();
        }
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open, closeDrawer]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") closeDrawer(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeDrawer]);

  useEffect(() => () => clearIdle(), [clearIdle]);

  const onLogout = () => {
    closeDrawer();
    logout();
    nav("/");
  };

  // Any interaction inside the sidebar resets the idle timer
  const onActivity = () => { if (open) armIdle(); };

  return (
    <>
      {/* Left-edge trigger: hover OR click to open */}
      <button
        type="button"
        data-testid="sidebar-trigger"
        aria-label="Open menu"
        aria-expanded={open}
        onMouseEnter={openDrawer}
        onClick={openDrawer}
        onFocus={openDrawer}
        className={`fixed left-0 top-0 z-40 h-screen w-2.5 flex items-center justify-start group transition-opacity ${
          open ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      >
        <span
          className="absolute left-0 top-1/2 -translate-y-1/2 h-16 w-1 rounded-r-full bg-[#D4A968]/40 group-hover:bg-[#D4A968] transition-colors"
          aria-hidden
        />
        <span
          className="absolute left-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#141417] border border-[#D4A968]/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
          aria-hidden
        >
          <ChevronRight className="w-3.5 h-3.5 text-[#D4A968]" strokeWidth={2.4} />
        </span>
      </button>

      {/* Backdrop — subtle dim so content stays visible */}
      <div
        data-testid="sidebar-backdrop"
        onClick={closeDrawer}
        aria-hidden
        className={`fixed inset-0 z-30 bg-black/30 backdrop-blur-[2px] transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Drawer */}
      <aside
        ref={asideRef}
        data-testid="sidebar"
        data-state={open ? "open" : "closed"}
        onMouseEnter={openDrawer}
        onMouseMove={onActivity}
        onClick={onActivity}
        onKeyDown={onActivity}
        onMouseLeave={armIdle}
        className={`fixed left-0 top-0 z-40 w-64 h-screen bg-[#0B0B0E] border-r border-zinc-800 flex flex-col shadow-2xl transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Brand mark */}
        <div className="px-3 pt-3 pb-2 border-b border-zinc-800/60 flex items-center gap-2">
          <VorionLogo variant="mark" size={32} testid="sidebar-brand" />
          <span className="text-sm font-bold tracking-widest text-[#D4A968]">VORION</span>
        </div>

        {/* Workspace badge */}
        <div className="p-3 border-b border-zinc-800">
          <div
            data-testid="workspace-switcher"
            title={current?.name || "Workspace"}
            className="w-full flex items-center gap-2 p-2 rounded-md bg-[#141417] border border-zinc-800 cursor-default"
          >
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#D4A968] to-[#7C5E2C] flex items-center justify-center text-black font-bold text-xs shrink-0">
              {(current?.name || "V").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-zinc-500 uppercase tracking-wider font-mono leading-tight">workspace</div>
              <div className="text-sm text-white font-semibold truncate leading-tight">{current?.name || "Vorion"}</div>
            </div>
          </div>
        </div>

        {current?.is_guest && (
          <div
            data-testid="guest-banner"
            title="You are a guest in this workspace"
            className="bg-[#D4A968]/10 border-b border-[#D4A968]/30 px-3 py-2 text-[10px] font-medium text-[#D4A968] uppercase tracking-wider flex items-center gap-2"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4A968]" />
            <span>You are a guest in this workspace</span>
          </div>
        )}

        <nav className="flex-1 p-2 overflow-y-auto space-y-0.5">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              title={label}
              onClick={closeDrawer}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-[#D4A968]/10 text-[#D4A968]"
                    : "text-zinc-400 hover:bg-[#141417] hover:text-white"
                }`
              }
            >
              <Icon className="w-4 h-4 shrink-0" strokeWidth={1.8} />
              <span>{label}</span>
            </NavLink>
          ))}
          {user?.is_global_admin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              title="Global Admin Panel"
              onClick={closeDrawer}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors mt-1 border-t border-zinc-800/60 pt-2 ${
                  isActive
                    ? "bg-[#D4A968]/10 text-[#D4A968]"
                    : "text-zinc-400 hover:bg-[#141417] hover:text-white"
                }`
              }
            >
              <ShieldCheck className="w-4 h-4 shrink-0" strokeWidth={1.8} />
              <span>Admin Panel</span>
            </NavLink>
          )}
        </nav>

        <div className="border-t border-zinc-800 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Avatar user={user} size="md" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">{current?.role || user?.role}</div>
            </div>
            <button
              onClick={() => { closeDrawer(); nav("/notifications"); }}
              className="p-1.5 rounded hover:bg-[#1B1B1F] text-zinc-400 hover:text-white"
              data-testid="notifications-btn"
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            title="Sign out"
            className="w-full flex items-center justify-center gap-1.5 text-[11px] text-zinc-500 hover:text-[#EF4444] py-1.5 transition-colors"
          >
            <LogOut className="w-3 h-3" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>
    </>
  );
}
